/**
 * Canonical Procurement Writers (Phase 2)
 * ─────────────────────────────────────────
 * Single, named, test-protected mutation surface for procurement state.
 *
 * Phase 2 charter:
 *   - mutation discipline only
 *   - explicit writers, validated transitions, append-only audit trail
 *   - NO automation, NO action queue, NO notifications, NO PO unification
 *
 * Direct calls to setPurchaseOrders / setReceivingRecords / setJobAllocations
 * outside this module are a regression — see procurementWritersPhase2.test.ts.
 *
 * NOTE: This module governs the canonical VendorPOContext PO model
 * (Draft → Submitted → Confirmed → Delivered) only. The legacy AppDataContext
 * PO model (warehouseExecution.ts) is documented as deferred in
 * src/data/procurement/PROCUREMENT_WRITE_AUDIT.md.
 */
import { useCallback } from "react";
import {
  useVendorPO,
  type PurchaseOrder,
  type POStatus,
  type ProcurementAuditEvent,
  type FieldMaterialFeedbackEvent,
  type FieldMaterialIssueType,
  type FieldMaterialSeverity,
  type SubstitutionRequest,
  type SubstitutionReason,
  type SubstitutionCompatibility,
  type SubstitutionCustomerImpact,
  type SubstitutionStatus,
} from "@/data/VendorPOContext";
import {
  useWarehouseInventory,
  type DiscrepancyType,
} from "@/data/WarehouseInventoryContext";
import { useFinanceWriters } from "./financeWriters";
import { FieldMaterialFeedbackService } from "@/services/field-material-feedback.service";
import { SubstitutionRequestService } from "@/services/substitution-request.service";

/* ── Status transition rules (canonical VendorPO model) ──
   Cancellation/rollback would require a status enum extension we don't
   have yet; for now, only forward transitions and idempotent stays are
   permitted. Anything else throws. */
const ALLOWED_PO_TRANSITIONS: Record<POStatus, POStatus[]> = {
  Draft: ["Draft", "Submitted"],
  Submitted: ["Submitted", "Confirmed"],
  Confirmed: ["Confirmed", "Delivered"],
  Delivered: ["Delivered"],
};

export function isValidPOStatusTransition(from: POStatus, to: POStatus): boolean {
  return (ALLOWED_PO_TRANSITIONS[from] ?? []).includes(to);
}

let _evtCounter = 0;
function nextAuditEventId(): string {
  _evtCounter += 1;
  return `paud-${Date.now().toString(36)}-${_evtCounter}-${Math.random().toString(36).slice(2, 6)}`;
}

export function createProcurementAuditEvent(input: {
  subjectType: ProcurementAuditEvent["subjectType"];
  subjectId: string;
  action: string;
  actor: string;
  reason?: string;
  metadata?: Record<string, unknown>;
}): ProcurementAuditEvent {
  return {
    id: nextAuditEventId(),
    subjectType: input.subjectType,
    subjectId: input.subjectId,
    action: input.action,
    actor: input.actor,
    ...(input.reason ? { reason: input.reason } : {}),
    createdAt: new Date().toISOString(),
    ...(input.metadata ? { metadata: input.metadata } : {}),
  };
}

function appendAudit(po: PurchaseOrder, evt: ProcurementAuditEvent): PurchaseOrder {
  return { ...po, auditEvents: [...(po.auditEvents ?? []), evt] };
}

/* ════════════════════════════════════════════════════════
   Hook — single entry point for procurement mutations
   ════════════════════════════════════════════════════════ */
export function useProcurementWriters() {
  const {
    purchaseOrders,
    setPurchaseOrders,
    fieldMaterialFeedback,
    setFieldMaterialFeedback,
    substitutionRequests,
    setSubstitutionRequests,
  } = useVendorPO();
  const {
    receivingRecords,
    setReceivingRecords,
    jobAllocations,
    confirmReceiving,
    allocateToJob,
  } = useWarehouseInventory();
  const finance = useFinanceWriters();

  /* 1. Append a new PO ───────────────────────────────── */
  const appendPurchaseOrder = useCallback(
    (input: Omit<PurchaseOrder, "auditEvents">, actor: string): PurchaseOrder => {
      // Refuse silent ETA confirmation at creation.
      if (input.etaConfirmed === true) {
        throw new Error(
          "appendPurchaseOrder: cannot create a PO with etaConfirmed=true. Use recordVendorAcknowledgement after creation.",
        );
      }
      const evt = createProcurementAuditEvent({
        subjectType: "po",
        subjectId: input.poId,
        action: "po.created",
        actor,
        metadata: { vendorId: input.vendorId, status: input.status, totalCost: input.totalCost },
      });
      const po: PurchaseOrder = { ...input, etaConfirmed: false, auditEvents: [evt] };
      setPurchaseOrders((prev) => [...prev, po]);
      return po;
    },
    [setPurchaseOrders],
  );

  /* 2. Update PO status (validated) ──────────────────── */
  const updatePurchaseOrderStatus = useCallback(
    (poId: string, nextStatus: POStatus, actor: string, reason?: string) => {
      setPurchaseOrders((prev) =>
        prev.map((po) => {
          if (po.poId !== poId) return po;
          if (!isValidPOStatusTransition(po.status, nextStatus)) {
            throw new Error(
              `updatePurchaseOrderStatus: illegal transition ${po.status} → ${nextStatus} for ${poId}`,
            );
          }
          if (po.status === nextStatus) return po; // idempotent
          const stamps: Partial<PurchaseOrder> = {};
          if (nextStatus === "Submitted" && !po.submittedAt) stamps.submittedAt = new Date().toISOString();
          if (nextStatus === "Confirmed" && !po.confirmedAt) stamps.confirmedAt = new Date().toISOString();
          if (nextStatus === "Delivered" && !po.deliveredAt) stamps.deliveredAt = new Date().toISOString();
          const evt = createProcurementAuditEvent({
            subjectType: "po",
            subjectId: poId,
            action: `po.status.${nextStatus.toLowerCase()}`,
            actor,
            reason,
            metadata: { from: po.status, to: nextStatus },
          });
          return appendAudit({ ...po, ...stamps, status: nextStatus }, evt);
        }),
      );
    },
    [setPurchaseOrders],
  );

  /* 3. Vendor acknowledgement — ONLY writer that confirms ETA ── */
  const recordVendorAcknowledgement = useCallback(
    (
      poId: string,
      input: { confirmedEta?: string; ackReference?: string; ackAt?: string },
      actor: string,
    ) => {
      const ts = input.ackAt ?? new Date().toISOString();
      setPurchaseOrders((prev) =>
        prev.map((po) => {
          if (po.poId !== poId) return po;
          const evt = createProcurementAuditEvent({
            subjectType: "po",
            subjectId: poId,
            action: "po.vendor_ack",
            actor,
            metadata: {
              confirmedEta: input.confirmedEta,
              ackReference: input.ackReference,
              previousEtaConfirmed: po.etaConfirmed === true,
            },
          });
          return appendAudit(
            {
              ...po,
              etaConfirmed: true,
              vendorAckAt: ts,
              ...(input.confirmedEta ? { confirmedEta: input.confirmedEta } : {}),
              ...(input.ackReference ? { ackReference: input.ackReference } : {}),
            },
            evt,
          );
        }),
      );
    },
    [setPurchaseOrders],
  );

  /* 4. Revise ETA WITHOUT confirming vendor ack ──────── */
  const reviseEstimatedDelivery = useCallback(
    (poId: string, eta: string, actor: string, reason: string) => {
      if (!reason || !reason.trim()) {
        throw new Error("reviseEstimatedDelivery: reason is required");
      }
      setPurchaseOrders((prev) =>
        prev.map((po) => {
          if (po.poId !== poId) return po;
          const evt = createProcurementAuditEvent({
            subjectType: "po",
            subjectId: poId,
            action: "po.eta.revised",
            actor,
            reason,
            metadata: { eta, previousEstimatedEta: po.estimatedEta, etaConfirmed: po.etaConfirmed === true },
          });
          // CRITICAL: do not flip etaConfirmed.
          return appendAudit({ ...po, estimatedEta: eta }, evt);
        }),
      );
    },
    [setPurchaseOrders],
  );

  /* 5. Receiving event — discrepancy blocks allocation ── */
  const recordReceivingEvent = useCallback(
    (
      input: {
        receivingRecordId: string;
        quantityReceived: number;
        discrepancy?: { type: DiscrepancyType; notes: string };
      },
      actor: string,
    ) => {
      // Delegates to Phase 0A-hardened confirmReceiving, which already
      // enforces discrepancy → allocationBlocked and never auto-allocates
      // damaged/wrong material.
      confirmReceiving(input.receivingRecordId, input.quantityReceived, input.discrepancy);
      const evt = createProcurementAuditEvent({
        subjectType: "receiving",
        subjectId: input.receivingRecordId,
        action: input.discrepancy ? "receiving.discrepancy" : "receiving.received",
        actor,
        ...(input.discrepancy ? { reason: input.discrepancy.notes } : {}),
        metadata: {
          quantityReceived: input.quantityReceived,
          discrepancyType: input.discrepancy?.type,
        },
      });
      setReceivingRecords((prev) =>
        prev.map((r) =>
          r.id === input.receivingRecordId
            ? { ...r, auditEvents: [...(r.auditEvents ?? []), evt] }
            : r,
        ),
      );
    },
    [confirmReceiving, setReceivingRecords],
  );

  /* 6. Allocation decision — guards against unresolved discrepancy ── */
  const recordAllocationDecision = useCallback(
    (
      input: { sku: string; jobId: string; jobLabel: string; quantity: number; override?: boolean; reason?: string },
      actor: string,
    ) => {
      const blocking = receivingRecords.find(
        (r) =>
          r.sku === input.sku &&
          r.jobId === input.jobId &&
          (r.allocationBlocked === true || r.discrepancy != null),
      );
      if (blocking) {
        // Override path is intentionally unsupported until the underlying
        // architecture has a discrepancy-resolution writer. Fail loud.
        throw new Error(
          `recordAllocationDecision: blocked by unresolved discrepancy on receiving ${blocking.id} (sku=${input.sku} job=${input.jobId}). Resolve receiving before allocating.`,
        );
      }
      allocateToJob(input.sku, input.jobId, input.jobLabel, input.quantity);
      // Audit trail for allocations is recorded at the receiving subject
      // (allocations themselves carry no audit array yet — keep schema small).
      const rec = receivingRecords.find((r) => r.sku === input.sku && r.jobId === input.jobId);
      if (rec) {
        const evt = createProcurementAuditEvent({
          subjectType: "allocation",
          subjectId: rec.id,
          action: "allocation.recorded",
          actor,
          ...(input.reason ? { reason: input.reason } : {}),
          metadata: { sku: input.sku, jobId: input.jobId, quantity: input.quantity },
        });
        setReceivingRecords((prev) =>
          prev.map((r) => (r.id === rec.id ? { ...r, auditEvents: [...(r.auditEvents ?? []), evt] } : r)),
        );
      }
    },
    [receivingRecords, jobAllocations, allocateToJob, setReceivingRecords],
  );

  /* 7. Append-only procurement note (PO subject) ─────── */
  const recordProcurementNote = useCallback(
    (poId: string, note: string, actor: string) => {
      if (!note || !note.trim()) throw new Error("recordProcurementNote: note required");
      setPurchaseOrders((prev) =>
        prev.map((po) => {
          if (po.poId !== poId) return po;
          const evt = createProcurementAuditEvent({
            subjectType: "po",
            subjectId: poId,
            action: "po.note",
            actor,
            metadata: { note },
          });
          return appendAudit(po, evt);
        }),
      );
    },
    [setPurchaseOrders],
  );

  /* ════════════════════════════════════════════════════════
     Phase 4 — Field Material Feedback (append-only)
     ════════════════════════════════════════════════════════ */

  /* 8. Record a field material feedback event ─────────── */
  const recordFieldMaterialFeedback = useCallback(
    (
      input: {
        jobId: string;
        issueType: FieldMaterialIssueType;
        severity: FieldMaterialSeverity;
        description: string;
        materialReference?: string;
        phase?: string;
        photoEvidence?: string[];
        /** Phase 6E — canonical lineage. */
        canonicalPoId?: string;
        lineageAmbiguous?: boolean;
        lineageAmbiguousReason?: string;
      },
      actor: string,
    ): FieldMaterialFeedbackEvent => {
      if (!input.jobId) throw new Error("recordFieldMaterialFeedback: jobId required");
      if (!input.issueType) throw new Error("recordFieldMaterialFeedback: issueType required");
      if (!input.severity) throw new Error("recordFieldMaterialFeedback: severity required");
      if (!input.description || !input.description.trim()) {
        throw new Error("recordFieldMaterialFeedback: description required");
      }
      if (!actor || !actor.trim()) {
        throw new Error("recordFieldMaterialFeedback: actor required");
      }
      // Phase 6E — validate canonical lineage if provided. No fabrication.
      if (input.canonicalPoId) {
        if (input.lineageAmbiguous) {
          throw new Error("recordFieldMaterialFeedback: canonicalPoId conflicts with lineageAmbiguous=true");
        }
        const exists = purchaseOrders.some((p) => p.poId === input.canonicalPoId);
        if (!exists) {
          throw new Error(`recordFieldMaterialFeedback: canonicalPoId ${input.canonicalPoId} does not resolve to any PurchaseOrder`);
        }
      }
      if (input.lineageAmbiguous && (!input.lineageAmbiguousReason || !input.lineageAmbiguousReason.trim())) {
        throw new Error("recordFieldMaterialFeedback: lineageAmbiguous requires lineageAmbiguousReason");
      }
      const id = `fmf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
      const evt: FieldMaterialFeedbackEvent = {
        id,
        jobId: input.jobId,
        createdAt: new Date().toISOString(),
        actor,
        issueType: input.issueType,
        severity: input.severity,
        description: input.description.trim(),
        ...(input.materialReference ? { materialReference: input.materialReference } : {}),
        ...(input.phase ? { phase: input.phase } : {}),
        ...(input.photoEvidence && input.photoEvidence.length
          ? { photoEvidence: [...input.photoEvidence] }
          : {}),
        resolved: false,
        ...(input.canonicalPoId ? { canonicalPoId: input.canonicalPoId } : {}),
        ...(input.lineageAmbiguous
          ? { lineageAmbiguous: true, lineageAmbiguousReason: input.lineageAmbiguousReason!.trim() }
          : {}),
      };
      setFieldMaterialFeedback((prev) => [...prev, evt]);
      // Persist to Supabase; rollback optimistic state if persistence fails.
      void FieldMaterialFeedbackService.insert(evt).catch((error) => {
        console.error("[procurementWriters] recordFieldMaterialFeedback persist failed:", error);
        setFieldMaterialFeedback((prev) => prev.filter((e) => e.id !== evt.id));
      });
      return evt;
    },
    [setFieldMaterialFeedback, purchaseOrders],
  );

  /* 9. Resolve an existing field material feedback event ── */
  const resolveFieldMaterialFeedback = useCallback(
    (eventId: string, actor: string, notes: string) => {
      if (!eventId) throw new Error("resolveFieldMaterialFeedback: eventId required");
      if (!actor || !actor.trim()) {
        throw new Error("resolveFieldMaterialFeedback: actor required");
      }
      if (!notes || !notes.trim()) {
        throw new Error("resolveFieldMaterialFeedback: notes required");
      }
      let found = false;
      const resolvedAt = new Date().toISOString();
      const previous = fieldMaterialFeedback.find((e) => e.id === eventId);
      setFieldMaterialFeedback((prev) =>
        prev.map((e) => {
          if (e.id !== eventId) return e;
          if (e.resolved) return e; // idempotent — append-only state transition once
          found = true;
          return {
            ...e,
            resolved: true,
            resolvedAt,
            resolvedBy: actor,
            resolutionNotes: notes.trim(),
          };
        }),
      );
      if (found) {
        // Persist resolution; rollback optimistic state if persistence fails.
        void FieldMaterialFeedbackService.update(eventId, {
          resolved: true,
          resolvedAt,
          resolvedBy: actor,
          resolutionNotes: notes.trim(),
        }).catch((error) => {
          console.error("[procurementWriters] resolveFieldMaterialFeedback persist failed:", error);
          if (!previous) return;
          setFieldMaterialFeedback((prev) => prev.map((e) => (e.id === eventId ? previous : e)));
        });
      }
      if (!found) {
        // Defer the throw to a microtask-safe check: after setState, missing id
        // means caller passed a non-existent event. Surface loud for tests.
        const exists = fieldMaterialFeedback.some((e) => e.id === eventId);
        if (!exists) {
          throw new Error(`resolveFieldMaterialFeedback: event ${eventId} not found`);
        }
      }
    },
    [setFieldMaterialFeedback, fieldMaterialFeedback],
  );

  /* ════════════════════════════════════════════════════════
     Phase 5 — Substitution Governance (append-only)
     ════════════════════════════════════════════════════════ */

  const VALID_REASONS: SubstitutionReason[] = [
    "vendor_unavailable", "vendor_delay", "cost_variance",
    "spec_change", "damage_replacement", "field_issue", "other",
  ];

  /* 10. Create a substitution governance request ─────── */
  const createSubstitutionRequest = useCallback(
    (
      input: {
        jobId: string;
        originalDescription: string;
        proposedDescription: string;
        reason: SubstitutionReason;
        originalMaterialReference?: string;
        proposedMaterialReference?: string;
        compatibilityAssessment?: SubstitutionCompatibility;
        compatibilityNotes?: string;
        customerImpact?: SubstitutionCustomerImpact;
        customerApprovalRequired?: boolean;
        /** Phase 6E — canonical lineage. */
        canonicalPoId?: string;
        lineageAmbiguous?: boolean;
        lineageAmbiguousReason?: string;
      },
      actor: string,
    ): SubstitutionRequest => {
      if (!input.jobId) throw new Error("createSubstitutionRequest: jobId required");
      if (!input.originalDescription || !input.originalDescription.trim()) {
        throw new Error("createSubstitutionRequest: originalDescription required");
      }
      if (!input.proposedDescription || !input.proposedDescription.trim()) {
        throw new Error("createSubstitutionRequest: proposedDescription required");
      }
      if (!input.reason) throw new Error("createSubstitutionRequest: reason required");
      if (!VALID_REASONS.includes(input.reason)) {
        throw new Error(`createSubstitutionRequest: invalid reason ${input.reason}`);
      }
      if (!actor || !actor.trim()) throw new Error("createSubstitutionRequest: actor required");

      // Phase 6E — lineage validation. No fabricated certainty.
      if (input.canonicalPoId) {
        if (input.lineageAmbiguous) {
          throw new Error("createSubstitutionRequest: canonicalPoId conflicts with lineageAmbiguous=true");
        }
        const exists = purchaseOrders.some((p) => p.poId === input.canonicalPoId);
        if (!exists) {
          throw new Error(`createSubstitutionRequest: canonicalPoId ${input.canonicalPoId} does not resolve to any PurchaseOrder`);
        }
      }
      if (input.lineageAmbiguous && (!input.lineageAmbiguousReason || !input.lineageAmbiguousReason.trim())) {
        throw new Error("createSubstitutionRequest: lineageAmbiguous requires lineageAmbiguousReason");
      }

      const req: SubstitutionRequest = {
        id: `sub-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
        jobId: input.jobId,
        createdAt: new Date().toISOString(),
        createdBy: actor,
        originalDescription: input.originalDescription.trim(),
        proposedDescription: input.proposedDescription.trim(),
        reason: input.reason,
        ...(input.originalMaterialReference ? { originalMaterialReference: input.originalMaterialReference } : {}),
        ...(input.proposedMaterialReference ? { proposedMaterialReference: input.proposedMaterialReference } : {}),
        compatibilityAssessment: input.compatibilityAssessment ?? "unknown",
        ...(input.compatibilityNotes ? { compatibilityNotes: input.compatibilityNotes } : {}),
        customerImpact: input.customerImpact ?? "unknown",
        customerApprovalRequired: input.customerApprovalRequired ?? false,
        status: "pending_review",
        ...(input.canonicalPoId ? { canonicalPoId: input.canonicalPoId } : {}),
        ...(input.lineageAmbiguous
          ? { lineageAmbiguous: true, lineageAmbiguousReason: input.lineageAmbiguousReason!.trim() }
          : {}),
      };
      setSubstitutionRequests((prev) => [...prev, req]);
      // Persist to Supabase; rollback optimistic state if persistence fails.
      void SubstitutionRequestService.insert(req).catch((error) => {
        console.error("[procurementWriters] createSubstitutionRequest persist failed:", error);
        setSubstitutionRequests((prev) => prev.filter((r) => r.id !== req.id));
      });
      return req;
    },
    [setSubstitutionRequests, purchaseOrders],
  );

  /* 11. Review a substitution request ─────────────────── */
  const reviewSubstitutionRequest = useCallback(
    (
      id: string,
      decision: "approve" | "reject" | "supersede",
      actor: string,
      input: {
        resolutionNotes: string;
        compatibilityAssessment?: SubstitutionCompatibility;
        compatibilityNotes?: string;
        customerImpact?: SubstitutionCustomerImpact;
      },
    ) => {
      if (!id) throw new Error("reviewSubstitutionRequest: id required");
      if (!actor || !actor.trim()) throw new Error("reviewSubstitutionRequest: actor required");
      if (!input || !input.resolutionNotes || !input.resolutionNotes.trim()) {
        throw new Error("reviewSubstitutionRequest: resolutionNotes required");
      }

      const target = substitutionRequests.find((r) => r.id === id);
      if (!target) throw new Error(`reviewSubstitutionRequest: request ${id} not found`);
      if (target.status !== "pending_review") {
        throw new Error(
          `reviewSubstitutionRequest: cannot transition from ${target.status} (request ${id})`,
        );
      }

      let nextStatus: SubstitutionStatus;
      let nextCompat: SubstitutionCompatibility = target.compatibilityAssessment;

      if (decision === "approve") {
        const compat = input.compatibilityAssessment ?? target.compatibilityAssessment;
        if (compat === "unknown") {
          throw new Error(
            "reviewSubstitutionRequest: approval requires compatibilityAssessment to be set (compatible | incompatible | conditional)",
          );
        }
        nextCompat = compat;
        nextStatus = "approved";
      } else if (decision === "reject") {
        if (input.compatibilityAssessment) nextCompat = input.compatibilityAssessment;
        nextStatus = "rejected";
      } else if (decision === "supersede") {
        if (input.compatibilityAssessment) nextCompat = input.compatibilityAssessment;
        nextStatus = "superseded";
      } else {
        throw new Error(`reviewSubstitutionRequest: unknown decision ${decision}`);
      }

      const resolvedAt = new Date().toISOString();
      setSubstitutionRequests((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          return {
            ...r,
            status: nextStatus,
            compatibilityAssessment: nextCompat,
            ...(input.compatibilityNotes ? { compatibilityNotes: input.compatibilityNotes } : {}),
            ...(input.customerImpact ? { customerImpact: input.customerImpact } : {}),
            resolvedAt,
            resolvedBy: actor,
            resolutionNotes: input.resolutionNotes.trim(),
          };
        }),
      );
      // Persist review outcome; rollback optimistic state if persistence fails.
      const reviewPatch: Partial<SubstitutionRequest> = {
        status: nextStatus,
        compatibilityAssessment: nextCompat,
        resolvedAt,
        resolvedBy: actor,
        resolutionNotes: input.resolutionNotes.trim(),
        ...(input.compatibilityNotes ? { compatibilityNotes: input.compatibilityNotes } : {}),
        ...(input.customerImpact ? { customerImpact: input.customerImpact } : {}),
      };
      void SubstitutionRequestService.update(id, reviewPatch).catch((error) => {
        console.error("[procurementWriters] reviewSubstitutionRequest persist failed:", error);
        setSubstitutionRequests((prev) => prev.map((r) => (r.id === id ? target : r)));
      });
    },
    [substitutionRequests, setSubstitutionRequests],
  );

  return {
    appendPurchaseOrder,
    updatePurchaseOrderStatus,
    recordVendorAcknowledgement,
    reviseEstimatedDelivery,
    recordReceivingEvent,
    recordAllocationDecision,
    recordProcurementNote,
    recordFieldMaterialFeedback,
    resolveFieldMaterialFeedback,
    createSubstitutionRequest,
    reviewSubstitutionRequest,
    // Phase 6C — finance writers (sole sanctioned mutators of PO.finance.*)
    ...finance,
  };
}
